import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import DashboardService from 'api/dashboard/DashboardService';
import { DashboardLayoutConfig, DashboardWidgetLayout } from '../types/DashboardLayoutTypes';
import { DEFAULT_LAYOUT, LAYOUT_VERSION, WIDGET_REGISTRY } from '../widgetRegistry';
import { GRID_COLS, LG_MIN_WIDTH_COLS, XXL_BREAKPOINT_PX } from '../utils/gridConstants';

// Adaptador de persistência: permite reutilizar a mesma engine de layout no painel
// principal (1 layout por utilizador) e no detalhe do ativo (1 layout por utilizador
// E por ativo), variando apenas a origem de leitura/gravação, o layout por defeito e
// o conjunto de widgets disponíveis (allowlist).
export type LayoutAdapter = {
    // Identidade estável do âmbito (ex.: 'main' ou 'asset:<vehicleId>'); usada para
    // reagir a mudanças de âmbito (ex.: trocar de ativo) sem depender da identidade do objeto.
    id: string;
    load: () => Promise<{ layout: string | null } | null>;
    save: (layout: string) => Promise<unknown>;
    defaultLayout: DashboardWidgetLayout[];
    version: number;
    // Quando definido, só estes widgets entram no layout (os demais são ignorados).
    allowlist?: Set<string>;
    // Aplica as migrações históricas específicas do painel principal (v13/v23/v30/v34).
    isMain?: boolean;
};

const MAIN_ADAPTER: LayoutAdapter = {
    id: 'main',
    load: () => DashboardService.getLayout(),
    save: (layout: string) => DashboardService.saveLayout(layout),
    defaultLayout: DEFAULT_LAYOUT,
    version: LAYOUT_VERSION,
    isMain: true,
};

// Migrações específicas do painel principal — preservadas tal como estavam.
const mergeMainWithRegistry = (saved: DashboardWidgetLayout[], savedVersion: number): DashboardWidgetLayout[] => {
    // V13: Dashboard redesign — all widgets start hidden (empty-first UX)
    if (savedVersion < 13) {
        return DEFAULT_LAYOUT.map(w => ({ ...w }));
    }

    // V18–V23: standardized widget sizes — reset w/h to registry defaults, keep visibility
    const forceSizeReset = savedVersion < 23;
    // V30: Location category widgets resized (aligned with dashboard mockup)
    const forceLocationSizeReset = savedVersion < 30;
    // V34: assetsAboveConsumption refactored from horizontal-bar (w:3) to table (w:6)
    const forceAssetsAboveConsumptionReset = savedVersion < 34;

    const result = saved.map(w => {
        const def = WIDGET_REGISTRY[w.i];
        if (!def) return w;

        const shouldResetSize = forceSizeReset
            || (forceLocationSizeReset && def.category === 'location')
            || (forceAssetsAboveConsumptionReset && def.id === 'assetsAboveConsumption');

        // Always sync minW/minH from registry
        const updated = {
            ...w,
            minW: def.minW,
            minH: def.minH,
            ...(shouldResetSize ? { w: def.defaultW, h: def.defaultH } : {}),
        };

        return updated;
    });

    const existingIds = new Set(result.map(w => w.i));

    // Add new widgets that are in registry but not in saved layout
    Object.keys(WIDGET_REGISTRY).forEach(id => {
        if (!existingIds.has(id)) {
            const def = WIDGET_REGISTRY[id];
            const maxY = result.reduce((max, w) => Math.max(max, w.y + w.h), 0);
            result.push({
                i: id,
                x: 0,
                y: maxY,
                w: def.defaultW,
                h: def.defaultH,
                minW: def.minW,
                minH: def.minH,
                // Widgets marcados addHidden entram escondidos (opt-in): aparecem no picker,
                // mas não no painel até o utilizador os adicionar.
                visible: !def.addHidden,
            });
        }
    });

    // Remove widgets that are in saved layout but no longer in registry
    return result.filter(w => WIDGET_REGISTRY[w.i] != null);
};

// Merge genérico (usado pelo dashboard do ativo): mantém as escolhas do utilizador,
// descarta widgets fora do allowlist/registry, sincroniza minW/minH e acrescenta
// (escondidos) os widgets novos do allowlist que ainda não estejam no layout gravado.
const mergeScopedWithRegistry = (
    saved: DashboardWidgetLayout[],
    adapter: LayoutAdapter,
): DashboardWidgetLayout[] => {
    const inScope = (id: string) => WIDGET_REGISTRY[id] != null && (!adapter.allowlist || adapter.allowlist.has(id));

    const result = saved
        .filter(w => inScope(w.i))
        .map(w => {
            const def = WIDGET_REGISTRY[w.i];
            return { ...w, minW: def.minW, minH: def.minH };
        });

    const existingIds = new Set(result.map(w => w.i));
    let maxY = result.reduce((max, w) => Math.max(max, w.y + w.h), 0);

    adapter.defaultLayout.forEach(d => {
        if (existingIds.has(d.i) || !inScope(d.i)) return;
        const def = WIDGET_REGISTRY[d.i];
        // Widgets novos entram escondidos (opt-in) no fundo do layout.
        result.push({ ...d, x: 0, y: maxY, minW: def.minW, minH: def.minH, visible: false });
        maxY += d.h;
    });

    return result;
};

const mergeWithRegistry = (
    saved: DashboardWidgetLayout[],
    savedVersion: number,
    adapter: LayoutAdapter,
): DashboardWidgetLayout[] =>
    adapter.isMain ? mergeMainWithRegistry(saved, savedVersion) : mergeScopedWithRegistry(saved, adapter);

const useDashboardLayout = (adapter: LayoutAdapter = MAIN_ADAPTER) => {
    const [layout, setLayout] = useState<DashboardWidgetLayout[]>(adapter.defaultLayout);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const layoutRef = useRef<DashboardWidgetLayout[]>(adapter.defaultLayout);
    // Mantém referência estável às funções do adaptador entre renders.
    const adapterRef = useRef(adapter);
    adapterRef.current = adapter;

    useEffect(() => {
        let cancelled = false;
        const fetchLayout = async () => {
            const current = adapterRef.current;
            try {
                setIsLoading(true);
                // Reset para o default deste âmbito antes de carregar (importante quando o
                // adaptador muda, ex.: trocar de ativo).
                setLayout(current.defaultLayout);
                layoutRef.current = current.defaultLayout;

                const response = await current.load();
                if (cancelled) return;
                if (response?.layout) {
                    const config: DashboardLayoutConfig = JSON.parse(response.layout);
                    const savedVersion = config.version ?? 1;
                    const merged = mergeWithRegistry(config.widgets, savedVersion, current);
                    setLayout(merged);
                    layoutRef.current = merged;

                    // Persist migrated layout so user doesn't re-migrate on every load
                    if (savedVersion < current.version) {
                        current.save(JSON.stringify({
                            version: current.version,
                            widgets: merged,
                        })).catch(() => { /* silent */ });
                    }
                }
            } catch {
                // Use default layout on error
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        fetchLayout();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [adapter.id]);

    const saveToBackend = useDebouncedCallback(async (widgets: DashboardWidgetLayout[]) => {
        try {
            const config: DashboardLayoutConfig = {
                version: adapterRef.current.version,
                widgets,
            };
            await adapterRef.current.save(JSON.stringify(config));
        } catch {
            // Silent fail - layout will be re-fetched on next page load
        }
    }, 500);

    const updateLayout = useCallback((newWidgets: DashboardWidgetLayout[]) => {
        setLayout(newWidgets);
        layoutRef.current = newWidgets;
        saveToBackend(newWidgets);
    }, [saveToBackend]);

    const updatePositions = useCallback((
        gridLayout: ReactGridLayout.Layout[],
        isBelowXxl = false
    ) => {
        const updated = layoutRef.current.map(widget => {
            const gridItem = gridLayout.find(g => g.i === widget.i);
            if (!gridItem) return widget;

            // Em ≤1920px, o w rendered é clamped para ≥ LG_MIN_WIDTH_COLS.
            // Se o w guardado já era menor (ex.: 3), manter o original para não perder
            // a configuração preferida do utilizador em ecrãs >1920.
            let nextW = gridItem.w;
            if (isBelowXxl && widget.w < LG_MIN_WIDTH_COLS && gridItem.w === LG_MIN_WIDTH_COLS) {
                nextW = widget.w;
            }

            return {
                ...widget,
                x: gridItem.x,
                y: gridItem.y,
                h: gridItem.h,
                w: nextW,
            };
        });
        updateLayout(updated);
    }, [updateLayout]);

    const toggleWidgetVisibility = useCallback((widgetId: string) => {
        const updated = layoutRef.current.map(widget => {
            if (widget.i === widgetId) {
                return { ...widget, visible: !widget.visible };
            }
            return widget;
        });
        updateLayout(updated);
    }, [updateLayout]);

    const addWidget = useCallback((widgetId: string) => {
        const def = WIDGET_REGISTRY[widgetId];
        if (!def) return;
        const isBelowXxl = typeof window !== 'undefined' && window.innerWidth <= XXL_BREAKPOINT_PX;
        const updated = layoutRef.current.map(widget => {
            if (widget.i === widgetId && !widget.visible) {
                const visible = layoutRef.current.filter(w => w.visible && w.i !== widgetId);

                // Find first position where the widget fits
                const maxY = visible.reduce((max, w) => Math.max(max, w.y + w.h), 0);
                const w = isBelowXxl ? Math.max(def.defaultW, LG_MIN_WIDTH_COLS) : def.defaultW;
                const h = def.defaultH;

                // Scan rows from top to find first gap
                for (let row = 0; row <= maxY; row++) {
                    for (let col = 0; col <= GRID_COLS - w; col++) {
                        const fits = !visible.some(v =>
                            col < v.x + v.w && col + w > v.x && row < v.y + v.h && row + h > v.y
                        );
                        if (fits) {
                            return { ...widget, visible: true, x: col, y: row, w, h };
                        }
                    }
                }

                // No gap found, place at bottom
                return { ...widget, visible: true, x: 0, y: maxY, w, h };
            }
            return widget;
        });
        updateLayout(updated);
    }, [updateLayout]);

    const removeWidget = useCallback((widgetId: string) => {
        const updated = layoutRef.current.map(widget => {
            if (widget.i === widgetId) {
                return { ...widget, visible: false };
            }
            return widget;
        });
        updateLayout(updated);
    }, [updateLayout]);

    const resetLayout = useCallback(async () => {
        const current = adapterRef.current;
        setLayout(current.defaultLayout);
        layoutRef.current = current.defaultLayout;
        try {
            await current.save(JSON.stringify({
                version: current.version,
                widgets: current.defaultLayout,
            }));
        } catch {
            // Silent fail
        }
    }, []);

    // Aplica uma disposição vinda de outra fonte (ex.: "Copiar de" outro ativo): faz o
    // mesmo merge com o registry que o load normal (minW/minH + completar o allowlist do
    // âmbito atual) e persiste. `rawConfig` é a string JSON gravada (ou null → default).
    const importLayout = useCallback((rawConfig: string | null) => {
        const current = adapterRef.current;
        let widgets = current.defaultLayout;
        if (rawConfig) {
            try {
                const config: DashboardLayoutConfig = JSON.parse(rawConfig);
                widgets = mergeWithRegistry(config.widgets, config.version ?? 1, current);
            } catch {
                // Config inválido → fica com o default do âmbito.
            }
        }
        updateLayout(widgets);
    }, [updateLayout]);

    return {
        layout,
        isEditMode,
        setIsEditMode,
        updateLayout,
        updatePositions,
        toggleWidgetVisibility,
        addWidget,
        removeWidget,
        resetLayout,
        importLayout,
        isLoading,
    };
};

// Constrói um adaptador para o dashboard de um ativo (layout por utilizador + por ativo).
export const buildAssetLayoutAdapter = (
    vehicleId: string,
    defaultLayout: DashboardWidgetLayout[],
    version: number,
    allowlist: Set<string>,
): LayoutAdapter => ({
    id: `asset:${vehicleId}`,
    load: () => DashboardService.getAssetLayout(vehicleId),
    save: (layout: string) => DashboardService.saveAssetLayout(vehicleId, layout),
    defaultLayout,
    version,
    allowlist,
});

export default useDashboardLayout;
